import { inject, Injectable } from '@angular/core';
import { updateDoc } from '@angular/fire/firestore';
import * as Sentry from '@sentry/angular';
import { AuthService } from '@auth';
import { IReplicableEntity } from '@shared/models/replicable-entity';
import { ReplicationSyncStatus } from '@shared/models/replication-sync-status';
import { FirestoreService } from '../firestore.service';
import { EntityRegistry } from './entity-registry.service';
import { ReplicationManager } from './replication-manager.service';

/**
 * Owns sync-status transitions for replicable entities and keeps the two
 * mirrors consistent: it writes the new status into Firestore (the local
 * source of truth) **and** replicates the mutation to the Desktop store so
 * the recovery conflict rule (`Synced` beats `UnSynced`, higher `updatedDate`
 * wins) always sees the latest status.
 *
 * Used by the backend-sync pipeline to move records through
 * `UnSynced → Syncing → Synced` (or roll back to `UnSynced`).
 */
@Injectable({ providedIn: 'root' })
export class SyncStatusManager {
  private readonly firestoreService = inject(FirestoreService);
  private readonly entityRegistry = inject(EntityRegistry);
  private readonly replicationManager = inject(ReplicationManager);
  private readonly authService = inject(AuthService);

  /**
   * Apply `status` to every entity, persisting to Firestore and replicating
   * the change to the Desktop. Mutates the passed entities in place so the
   * caller can keep working with the updated objects.
   *
   * @returns the entities that were successfully updated in Firestore.
   */
  async applyStatus<T extends IReplicableEntity>(
    entityType: string,
    entities: T[],
    status: ReplicationSyncStatus,
    statusMessage?: string
  ): Promise<T[]> {
    if (!entities?.length) return [];

    const config = this.entityRegistry.getConfig(entityType);
    if (!config) {
      console.warn(`[SyncStatusManager] Entity type '${entityType}' is not registered.`);
      return [];
    }

    const now = new Date();
    const updated: T[] = [];

    const sanitizedProfile = this.getSanitizedUserProfile();

    // Sequential per-record writes (one updateDoc + one replication emit
    // each). The working set is bounded by the caller's query `limit`, so
    // this stays cheap; kept sequential for predictable ordering and
    // back-pressure. Switch to batched writes (Promise.all / Firestore
    // writeBatch) only if a large backlog ever makes this a hotspot.
    for (const entity of entities) {
      const localId = entity.metadata?.localId;
      if (!localId) continue;

      // Reflect the change on the in-memory copy so the replicated payload
      // (and the caller's object) is the latest state.
      entity.metadata!.syncInfo = {
        ...(entity.metadata!.syncInfo ?? {}),
        syncStatus: status,
        syncStatusMessage: statusMessage ?? entity.metadata!.syncInfo?.syncStatusMessage,
        syncedDate: status === ReplicationSyncStatus.Synced ? now : entity.metadata!.syncInfo?.syncedDate,
        syncedBy: status === ReplicationSyncStatus.Synced
          ? sanitizedProfile
          : entity.metadata!.syncInfo?.syncedBy
      };
      entity.metadata!.updatedDate = now;
      entity.metadata!.updatedBy = sanitizedProfile;

      // Surgical field-merge — only the sync-status fields are written, so a
      // concurrent write to the same document (e.g. a recovery restore) is
      // not clobbered. Mirrors ReplicationManager.markReplicated().
      const fieldUpdates: Record<string, any> = {
        'metadata.syncInfo.syncStatus': status,
        'metadata.updatedDate': now,
        'metadata.updatedBy': sanitizedProfile
      };
      if (statusMessage !== undefined) {
        fieldUpdates['metadata.syncInfo.syncStatusMessage'] = statusMessage;
      }
      if (status === ReplicationSyncStatus.Synced) {
        fieldUpdates['metadata.syncInfo.syncedDate'] = now;
        fieldUpdates['metadata.syncInfo.syncedBy'] = sanitizedProfile;
      }

      // Entity-specific companion fields (e.g. the legacy Order 2.5 sync
      // vocabulary) ride along in the same surgical write so the two
      // representations can never diverge.
      if (config.additionalStatusFields) {
        const extraFields = config.additionalStatusFields(entity, status, statusMessage);
        for (const [fieldPath, value] of Object.entries(extraFields ?? {})) {
          if (value !== undefined) {
            fieldUpdates[fieldPath] = value;
          }
        }
      }

      try {
        const ref = this.firestoreService.doc(`${config.firestoreCollection}/${localId}`);
        // updateDoc (not setDoc): fails if the doc was deleted rather than
        // resurrecting it, and never overwrites unrelated fields.
        await updateDoc(ref, fieldUpdates);
        updated.push(entity);

        // Mirror the new status to the Desktop (idempotent upsert by localId).
        this.replicationManager.emitReplicationEvent({
          entityType,
          operation: 'update',
          entity,
          firestoreCollection: config.firestoreCollection,
          timestamp: now
        });
      } catch (error) {
        console.error(
          `[SyncStatusManager] Failed to set status '${status}' on ${entityType}/${localId}`,
          error
        );
        Sentry.captureException(error);
      }
    }

    return updated;
  }

  private getSanitizedUserProfile(): any {
    const profile = { ...this.authService.userProfile };
    delete profile.picture;
    return profile;
  }
}
